<template>
  <section class="block">
    <h1 v-if="template.name !== ''">
      PDFテンプレート: <span>{{ template.name }}</span>
    </h1>
    <h1 v-else>PDFテンプレート</h1>

    <Snackbar :option="snackbar"></Snackbar>

    <div class="main-content">
      <div class="form">
        <table>
          <tbody>
            <tr>
              <th>ファイル名</th>
              <td><input type="text" class="field" v-model="template.name" /></td>
            </tr>
            <tr>
              <th>設定</th>
              <td>
                <div class="checkbox">
                  <label>
                    <input type="checkbox" v-model="template.setting['enable-page-break']" />
                    <span>ページ分割</span>
                  </label>
                  <label>
                    <input type="checkbox" v-model="template.setting['kintone_data']" @change="handleKintoneDataChange" />
                    <span>kintoneデータ設定</span>
                  </label>
                </div>
              </td>
            </tr>
            <tr v-if="template.setting['kintone_data'] && !isLoadingGetKinTonApp">
              <th>キントーンアプリ</th>
              <td>
                <div class="select">
                  <select v-model="kinTonAppId" @change="handleChangeKinToneApp" :disabled="isLoadingGetKinTonApp">
                    <option value="">{{ isLoadingGetKinTonApp ? 'Loading ...' : '1つ選択してください' }}</option>
                    <option v-for="(item, index) in kinToneAppData" :key="index" :value="item.appId">{{ item.name }}</option>
                  </select>
                </div>
              </td>
            </tr>
            <tr v-if="template.setting['kintone_data'] && !isLoadingGetKinTonApp">
              <th>キントーンフィールド</th>
              <td>
                <div class="select">
                  <select v-model="kinToneFieldKey" :disabled="!kinTonAppId || isLoadingGetKinTonField">
                    <option value="">{{ isLoadingGetKinTonField ? 'Loading ...' : '1つ選択してください' }}</option>
                    <option v-for="(item, index) in kinToneFieldData" :key="index" :value="item">
                      {{ item }}
                    </option>
                  </select>
                </div>
              </td>
            </tr>
            <tr>
              <th>PDF</th>
              <td>
                <input type="file" hidden accept="application/pdf" ref="pdfInput" @change="handelInputChange($event)" @click="handleInputClick($event)" />
                <div class="dropzone" @drop="handleDrop" @dragover="handleDragover" @click="openFileDialog">
                  <p v-if="!template.pdf && !newPdf.name">ここにPDFファイルをドロップまたはクリックして選択してください</p>
                  <p v-if="pdfDataUri && newPdf.name === ''">
                    <a :href="pdfDataUri" download>PDFをダウンロード</a>
                  </p>
                  <div class="prepared" v-if="newPdf.name !== ''">
                    <figure>
                      <font-awesome-icon icon="file-pdf" />
                      <span>{{ newPdf.name }}</span>
                    </figure>
                    <font-awesome-icon icon="minus-circle" @click="resetPdf" />
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="edit && template.created">
              <th>作成日</th>
              <td>{{ new Date(template.created).toLocaleString() }}</td>
            </tr>
            <tr v-if="edit && template.updated">
              <th>更新日時</th>
              <td>{{ new Date(template.updated).toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>

        <nav>
          <button class="btn btn-secondary" v-if="!edit" @click="add" :disabled="isAdding">
            <font-awesome-icon v-if="isAdding" icon="spinner" spin class="me-2" />
            {{ isAdding ? 'Adding...' : '追加' }}
          </button>
          <button class="btn btn-secondary" v-if="edit" @click="update" :disabled="isUpdating">
            <font-awesome-icon v-if="isUpdating" icon="spinner" spin class="me-2" />
            {{ isUpdating ? 'Updating...' : 'アップデート' }}
          </button>
          <button class="btn btn-danger" v-if="edit" @click="remove">消去</button>
        </nav>
      </div>

      <div class="notice-block">
        <h3 class="notice-title">【使用上の注意事項】</h3>
        <p class="notice-description">
          ここでのチェックはonboardの帳票テンプレートPDFファイルの注釈設定の正しさを必ず保証するものではありません。<br />
          現在、注釈設定のうち確認が出来るのは以下の内容です。
        </p>
        <ul class="notice-list">
          <li>項目名の重複</li>
          <li>表示順の重複</li>
          <li>半角の「」」のチェック</li>
          <li>全角の「＝」、「＠」、「＃」、「＊」のチェック</li>
          <li>改行のチェック</li>
        </ul>
      </div>
    </div>

    <ValidationAnnotationsTable
      v-if="validationResult.errors.length"
      :validation-errors="validationResult.errors"
      :annotations="template.annotations"
      :total-items="validationResult.totalAnnotations"
      :error-count="validationResult.totalErrors"
    />
  </section>
  <Dialog ref="remove" class="dialog">
    <h4 class="title">確認する</h4>
    <div class="message">
      <p class="text">本当にこのプロフィールを削除しますか?</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="removeCancel">キャンセル</button>
      <button class="btn btn-danger" @click="removeConfirmed">消去</button>
    </div>
  </Dialog>
  <Dialog ref="error" class="dialog">
    <h4 class="title">Error</h4>
    <div class="message">
      <p class="text">{{ error.message }}</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="errorClose">Close</button>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Dialog from '@/components/Dialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import ValidationAnnotationsTable from '@/components/ValidationAnnotationsTable.vue';

import {
  checkFieldConflictsPerPage,
  FlattenedAnnotation,
  groupAnnotationsByPage,
  parseAnnotationString,
  validateAnnotationsWithSummary,
  ValidationResult,
} from '@/lib/validateAnnotation';

import { Template, TemplateData, TemplateSetting } from '@/lib/template';
import router from '@/router';
import { notify } from '@kyvg/vue3-notification';

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { getKinToneApps, getKinTonFields, KinToneAppData, KintoneFieldData } from '@/lib/kinTone';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default defineComponent({
  name: 'TemplateEditor',
  components: {
    Dialog,
    Snackbar,
    ValidationAnnotationsTable,
  },
  props: {
    edit: Boolean,
    tmplSlug: String,
    prjSlug: {
      type: String,
      require: true,
    },
    orgSlug: {
      type: String,
      require: true,
    },
  },
  data() {
    return {
      template: {
        pdf: '',
        name: '',
        setting: {} as TemplateSetting,
        created: undefined,
        updated: undefined,
        annotations: [] as FlattenedAnnotation[],
        appId: '',
        field_key: '',
      } as TemplateData,
      message: '',
      pdfDataUri: '',
      newPdf: {
        name: '',
        data: '',
        file: null as File | null,
      },
      snackbar: {
        type: '',
        message: '',
      },
      error: {
        message: '',
        back: false,
      },
      validationResult: {
        errors: [],
        totalAnnotations: 0,
        totalErrors: 0,
      } as ValidationResult,
      kinToneAppData: [] as KinToneAppData[],
      kinTonAppId: '',
      kinToneFieldData: [] as KintoneFieldData[],
      kinToneFieldKey: '',
      isUpdating: false,
      isAdding: false,
      isLoadingGetKinTonApp: false,
      isLoadingGetKinTonField: false,
    };
  },
  setup() {
    return {
      validateAnnotationsWithSummary,
      parseAnnotationString,
      checkFieldConflictsPerPage,
      groupAnnotationsByPage,
    };
  },

  async mounted() {
    if (!this.orgSlug) {
      this.error.message = '組織が指定されていません';
      this.error.back = true;
      (this.$refs.error as InstanceType<typeof Dialog>)?.open();
      return;
    }
    if (!this.prjSlug) {
      this.error.message = 'プロフィールが指定されておりません！';
      this.error.back = true;
      (this.$refs.error as InstanceType<typeof Dialog>)?.open();
      return;
    }
    if (this.tmplSlug) {
      try {
        const tmpl = new Template(this.orgSlug, this.prjSlug, this.tmplSlug);
        const data = await tmpl.get();

        if (!data) {
          this.error.message = 'テンプレートが見つかりません！';
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
          return;
        }

        const setting = data.setting as TemplateSetting;
        this.template = data;
        this.pdfDataUri = `data:application/pdf;base64,${data.pdf}`;
        this.kinTonAppId = setting.app_id;
        this.kinToneFieldKey = setting.field_key;
        if (setting.kintone_data) {
          await this.getKinToneApps();
          if (setting.app_id && setting.field_key) {
            await this.getKinToneFields();
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    }
  },
  methods: {
    openFileDialog() {
      (this.$refs.pdfInput as HTMLInputElement).click();
    },
    validate(pdf: boolean): boolean {
      if (this.template.name === '') {
        this.snackbar.type = 'warning';
        this.snackbar.message = "テンプレート名を空にすることはできません！";
        this.disableLoading();
        return false;
      }
      if (pdf && this.newPdf.name === '') {
        this.snackbar.type = 'warning';
        this.snackbar.message = "テンプレートPDFを空にすることはできません！";
        this.disableLoading();
        return false;
      }

      this.snackbar.message = '';
      return true;
    },
    async readPdfAnnotations(file: File): Promise<{ success: true; data: any[] } | { success: false; error: Error }> {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const pdf = await pdfjsLib.getDocument({ data: uint8Array }).promise;
        const annotationPromises = Array.from({ length: pdf.numPages }, (_, index) =>
          (async () => {
            const pageNumber = index + 1;
            const page = await pdf.getPage(pageNumber);
            const annotations = await page.getAnnotations();

            return annotations.length ? { page: pageNumber, annotations: annotations } : null;
          })()
        );

        const results = (await Promise.all(annotationPromises)).filter(Boolean) as {
          page: number;
          annotations: any[];
        }[];

        if (!results.length) return { success: false, error: new Error('空の注釈') };

        return { success: true, data: results };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error : new Error('不明なエラー') };
      }
    },
    disableLoading() {
      this.isAdding = false;
      this.isUpdating = false;
      this.isLoadingGetKinTonField = false;
      this.isLoadingGetKinTonApp = false;
    },
    async add() {
      if (this.isAdding) return;
      this.isAdding = true;

      if (!this.validate(true) || !this.orgSlug) {
        return;
      }
      if (this.template.setting['kintone_data'] && !this.kinTonAppId) {
        this.snackbar.type = 'warning';
        this.snackbar.message = 'Kintoneアプリを選択してください！';
        this.disableLoading();
        return;
      }
      if (this.kinTonAppId && !this.kinToneFieldKey) {
        this.snackbar.type = 'warning';
        this.snackbar.message = 'Kintoneレコードを選択してください！';
        this.disableLoading();
        return;
      }
      this.validationResult = {
        errors: [],
        totalAnnotations: 0,
        totalErrors: 0,
      };

      this.template.annotations = [];

      const result = await this.readPdfAnnotations(this.newPdf.file as File);

      if (!result.success) {
        console.warn('PDFの注釈を読み込めませんでした：', result.error);
        this.snackbar.type = 'warning';
        this.snackbar.message = `PDFから注釈を取得できませんでした： ${result.error.message}`;
        this.disableLoading();
        return;
      }

      try {
        const tmpl = new Template(this.orgSlug, this.prjSlug);
        this.template.pdf = this.newPdf.data;

        this.template.annotations = result.data.flatMap((pageAnnotation) =>
          pageAnnotation.annotations.map((annotation: any) => ({
            ...annotation,
            page: pageAnnotation.page,
            ...(annotation?.contents ? parseAnnotationString(annotation.contents) : {}),
          }))
        );

        const annotationsByPage = groupAnnotationsByPage(this.template.annotations);

        const freeTextResult = Object.entries(annotationsByPage).reduce(
          (acc, [pageStr, annotations]) => {
            const page = Number(pageStr);

            if (!annotations.some((a) => a.subtype === 'FreeText')) return acc;

            const result = validateAnnotationsWithSummary(annotations);

            if (result.totalErrors > 0) {
              acc.errors.push(...result.errors);
              acc.totalAnnotations += result.totalAnnotations;
              acc.totalErrors += result.totalErrors;
              acc.pageSummaries.push(`page ${page} (${result.totalErrors} errors)`);
            }

            return acc;
          },
          {
            errors: [] as any,
            totalAnnotations: 0,
            totalErrors: 0,
            pageSummaries: [] as string[],
          }
        );

        if (freeTextResult.totalErrors > 0) {
          this.validationResult = {
            errors: freeTextResult.errors,
            totalAnnotations: freeTextResult.totalAnnotations,
            totalErrors: freeTextResult.totalErrors,
          };

          this.snackbar.type = 'warning';
          this.snackbar.message = `PDFのバリデーションに失敗しました： ${freeTextResult.pageSummaries.join(', ')}`;
          return;
        }

        const formFieldErrors = checkFieldConflictsPerPage(this.template.annotations);
        const totalErrors = formFieldErrors.length;
        if (totalErrors > 0) {
          this.validationResult = {
            errors: formFieldErrors,
            totalAnnotations: this.template.annotations.length,
            totalErrors,
          };

          this.snackbar.type = 'warning';
          this.snackbar.message = 'フォームフィールドの競合により、PDFの検証に失敗しました。';
          return;
        }
        this.template.appId = this.kinTonAppId;
        this.template.field_key = this.kinToneFieldKey;

        const resultAddTemplate = await tmpl.add(this.template);

        if (resultAddTemplate.slug) {
          notify({
            text: 'テンプレートを追加しました！',
            type: 'success',
          });
          console.info('Template added!');
          router.replace(`/organizations/${this.orgSlug}/projects/${this.prjSlug}/template/${resultAddTemplate.slug}`);
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = resultAddTemplate.error || 'テンプレートを作成できませんでした！';

          this.validationResult = {
            errors: resultAddTemplate?.validationErrors || [],
            totalAnnotations: resultAddTemplate?.totalAnnotations || 0,
            totalErrors: resultAddTemplate?.totalErrors || 0,
          };
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      } finally {
        this.disableLoading();
      }
    },
    async update() {
      if (this.isUpdating) return;
      this.isUpdating = true;
      if (!this.validate(false) || !this.orgSlug) {
        return;
      }

      this.validationResult = {
        errors: [],
        totalAnnotations: 0,
        totalErrors: 0,
      };

      this.template.annotations = [];
      if (this.template.setting['kintone_data']) {
        if (!this.kinTonAppId) {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'Kintoneアプリを選択してください！';
          this.disableLoading();
          return;
        }
        if (this.kinTonAppId && !this.kinToneFieldKey) {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'Kintoneレコードを選択してください！';
          this.disableLoading();
          return;
        }
      }

      try {
        const tmpl = new Template(this.orgSlug, this.prjSlug, this.tmplSlug);
        if (this.template.data !== '') {
          this.template.pdf = this.newPdf.data;
        }

        if (this.newPdf.file) {
          const result = await this.readPdfAnnotations(this.newPdf.file as File);
          if (!result.success) {
            console.warn('PDFの注釈を読み込めませんでした：', result.error);
            this.snackbar.type = 'warning';
            this.snackbar.message = `PDFから注釈を取得できませんでした： ${result.error.message}`;
            this.disableLoading();
            return;
          }

          this.template.annotations = result.data.flatMap((pageAnnotation) =>
            pageAnnotation.annotations.map((annotation: any) => ({
              ...annotation,
              page: pageAnnotation.page,
              ...(annotation?.contents ? parseAnnotationString(annotation.contents) : {}),
            }))
          );

          const annotationsByPage = groupAnnotationsByPage(this.template.annotations);

          const freeTextResult = Object.entries(annotationsByPage).reduce(
            (acc, [pageStr, annotations]) => {
              const page = Number(pageStr);

              if (!annotations.some((a) => a.subtype === 'FreeText')) return acc;

              const result = validateAnnotationsWithSummary(annotations);

              if (result.totalErrors > 0) {
                acc.errors.push(...result.errors);
                acc.totalAnnotations += result.totalAnnotations;
                acc.totalErrors += result.totalErrors;
                acc.pageSummaries.push(`page ${page} (${result.totalErrors} errors)`);
              }

              return acc;
            },
            {
              errors: [] as any,
              totalAnnotations: 0,
              totalErrors: 0,
              pageSummaries: [] as string[],
            }
          );

          if (freeTextResult.totalErrors > 0) {
            this.validationResult = {
              errors: freeTextResult.errors,
              totalAnnotations: freeTextResult.totalAnnotations,
              totalErrors: freeTextResult.totalErrors,
            };

            this.snackbar.type = 'warning';
            this.snackbar.message = `PDFのバリデーションに失敗しました： ${freeTextResult.pageSummaries.join(', ')}`;
            return;
          }

          const formFieldErrors = checkFieldConflictsPerPage(this.template.annotations);
          const totalErrors = formFieldErrors.length;
          if (totalErrors > 0) {
            this.validationResult = {
              errors: formFieldErrors,
              totalAnnotations: this.template.annotations.length,
              totalErrors,
            };

            this.snackbar.type = 'warning';
            this.snackbar.message = 'フォームフィールドの衝突が原因で、PDFの検証に失敗しました。';
            return;
          }
        }
        this.template.appId = this.kinTonAppId;
        this.template.field_key = this.kinToneFieldKey;

        const resultAddTemplate = await tmpl.update(this.template as TemplateData);
        if (resultAddTemplate.slug) {
          this.snackbar.type = 'success';
          this.snackbar.message = 'プロファイルが更新されました！';
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = resultAddTemplate.error || 'テンプレートを作成できませんでした！';

          this.validationResult = {
            errors: resultAddTemplate?.validationErrors || [],
            totalAnnotations: resultAddTemplate?.totalAnnotations || 0,
            totalErrors: resultAddTemplate?.totalErrors || 0,
          };
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      } finally {
        this.disableLoading();
      }
    },
    remove() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.open();
    },
    async removeConfirmed() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
      if (!this.orgSlug) {
        return;
      }
      try {
        const tmpl = new Template(this.orgSlug, this.prjSlug, this.tmplSlug);
        if (await tmpl.delete()) {
          console.info('Template deleted!');
          router.replace(`/organizations/${this.orgSlug}/projects/${this.prjSlug}`);
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'このテンプレートの削除に失敗しました！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    removeCancel() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
    },
    handleInputClick(event: Event) {
      if (this.edit) {
        event.preventDefault();
      }
    },
    async handelInputChange(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.files?.length) {
        const file = target.files[0];
        if (file.type.toLowerCase() !== 'application/pdf') {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'PDFファイルはPDFでなければなりません！';
          return;
        }
        this.template.name = file.name.replace(/\.[^/.]+$/, '');
        this.newPdf.name = file.name;
        this.newPdf.file = file;
        this.newPdf.data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.addEventListener('load', () => {
            const iconUrl = reader.result as string;
            resolve(iconUrl.split(',')[1]);
          });
        });
      }
    },
    async handleDrop(event: DragEvent) {
      event.preventDefault();

      if (!event.dataTransfer) return;

      const item = event.dataTransfer.items[0];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (!file) return;
        if (file.type.toLowerCase() !== 'application/pdf') {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'PDFファイルはPDFでなければなりません';
          return;
        }
        this.template.name = file.name.replace(/\.[^/.]+$/, '');
        this.newPdf.name = file.name;
        this.newPdf.file = file;
        this.newPdf.data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.addEventListener('load', () => {
            const iconUrl = reader.result as string;
            resolve(iconUrl.split(',')[1]);
          });
        });
      }
    },
    async handleDragover(event: Event) {
      event.preventDefault();
    },
    async handleChangeKinToneApp(event: Event) {
      const target = event.target as HTMLSelectElement;
      this.kinTonAppId = target.value;
      if (this.kinTonAppId) {
        try {
          this.isLoadingGetKinTonField = true;
          this.kinToneFieldData = await getKinTonFields(this.kinTonAppId, this.orgSlug as string);
          this.kinToneFieldKey = '';
          this.isLoadingGetKinTonField = false;
        } catch (e) {
          this.isLoadingGetKinTonField = false;
          this.snackbar.type = 'warning';
          this.snackbar.message = 'Kintoneレコードの取得に失敗しました';
          console.warn(e);
        }
      } else {
        this.kinToneFieldData = [];
        this.kinToneFieldKey = '';
      }
    },
    resetPdf() {
      this.newPdf = {
        name: '',
        data: '',
        file: null,
      };
      this.snackbar = {
        type: '',
        message: '',
      };
      this.validationResult = {
        errors: [],
        totalAnnotations: 0,
        totalErrors: 0,
      };
      this.template.annotations = [];
      this.template.name = '';
      (this.$refs.pdfInput as HTMLInputElement).value = '';
    },
    errorClose() {
      (this.$refs.error as InstanceType<typeof Dialog>)?.close();
      this.error.back && this.$router.back();
    },
    async handleKintoneDataChange(event: Event) {
      const target = event.target as HTMLInputElement;
      this.template.setting['kintone_data'] = target.checked;
      if (this.template.setting['kintone_data']) {
        !this.kinToneAppData.length && (await this.getKinToneApps());
        !this.kinToneFieldData.length && this.kinTonAppId && (await this.getKinToneFields());
        this.kinTonAppId = this.kinTonAppId || this.template?.setting?.app_id || '';
        this.kinToneFieldKey = this.kinToneFieldKey || this.template?.setting?.field_key || '';
      }
    },
    // get kintone
    async getKinToneApps() {
      try {
        this.isLoadingGetKinTonApp = true;
        this.kinToneAppData = await getKinToneApps(this.orgSlug as string);
        this.isLoadingGetKinTonApp = false;
      } catch (e) {
        this.isLoadingGetKinTonApp = false;
        this.template.setting['kintone_data'] = false
        notify({
            text: 'Kintoneのセットアップについては、管理者にご連絡ください。',
            type: 'warn',
          });
        console.warn(e);
      }
    },
    async getKinToneFields() {
      try {
        this.isLoadingGetKinTonField = true;
        this.kinToneFieldData = await getKinTonFields(this.kinTonAppId, this.orgSlug as string);
        this.isLoadingGetKinTonField = false;
      } catch (error) {
        this.kinTonAppId = '';
        this.kinToneFieldKey = '';
        this.snackbar.type = 'warning';
        this.snackbar.message = 'Kintoneレコードを取得できませんでした。';
        console.warn(error);
        this.isLoadingGetKinTonField = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/assets/const.scss' as *;
@use '@/assets/dialog.scss' as *;

.block {
  th {
    width: 25%;
  }

  td {
    width: 75%;
  }

  .main-content {
    display: flex;
    gap: 20px;
    margin: 20px 10px;

    @media (min-width: $tablet) {
      margin: 20px;
      gap: 30px;
    }

    @media (min-width: $desktop) {
      margin: 20px 40px;
      gap: 40px;
    }

    // Responsive layout - mobile sẽ stack vertical
    @media (max-width: #{$tablet - 1px}) {
      flex-direction: column;
    }
  }

  .notice-block {
    flex: 6;
    margin: 0;
    padding: 20px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #60a5fa;
    border-radius: 6px;

    .notice-title {
      margin: 0 0 16px 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #374151;
    }

    .notice-description {
      margin: 0 0 16px 0;
      line-height: 1.6;
      color: #6b7280;
      font-size: 0.95rem;
    }

    .notice-list {
      margin: 0 0 16px 0;
      padding: 0;
      list-style: none;

      li {
        margin-bottom: 8px;
        color: #6b7280;
        font-size: 0.9rem;
        line-height: 1.5;
        padding-left: 16px;
        position: relative;

        &:before {
          content: '•';
          color: #60a5fa;
          position: absolute;
          left: 0;
        }
      }
    }
  }

  .form {
    flex: 6;
    margin: 0;

    table {
      width: 100%;
    }

    th {
      padding: 5px;
      font-weight: normal;
    }

    td {
      padding: 5px;
    }

    .field {
      width: 100%;
    }

    .checkbox {
      display: flex;
      flex-direction: column;

      span {
        margin-left: 5px;
      }
    }

    .select {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: 10px;

      select {
        width: 100%;
        min-width: 200px;
      }
    }

    .qrlink {
      word-break: break-all;

      a {
        text-decoration: none;
      }

      svg {
        cursor: pointer;
        margin-left: 10px;
      }
    }

    .dropzone {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 180px;
      padding: 10px 15px;
      border: 1px solid var(--bs-gray-400);
      border-radius: 5px;
      cursor: pointer;

      p {
        position: relative;
        margin: 0;
        color: var(--bs-gray-600);

        a {
          text-decoration: none;
        }
      }

      .prepared {
        position: relative;

        figure {
          display: flex;
          flex-direction: column;

          span {
            margin-top: 5px;
            word-break: break-all;
          }

          svg {
            margin: auto;
            font-size: 4rem;
          }
        }

        & > svg {
          cursor: pointer;
          position: absolute;
          top: -0.5rem;
          right: -0.5rem;
          font-size: 1.8rem;
          color: var(--bs-danger);
        }
      }
    }

    nav {
      display: flex;
      margin-top: 20px;

      button {
        margin-right: 20px;

        &:last-child:not(:first-child) {
          margin-left: auto;
          margin-right: 0;
        }
      }
    }
  }
}

.dialog {
  @include dialog();
}
</style>
